"use client";

import { Provider } from "@rollbar/react";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import Rollbar from "rollbar";

import { getClientConfig } from "@/lib/rollbar";

interface RollbarProviderProps {
  children: ReactNode;
}

/**
 * Context to track whether Rollbar is enabled.
 * Used by child components to safely check before using useRollbar().
 */
const RollbarEnabledContext = createContext<boolean>(false);

/**
 * Hook to check if Rollbar is enabled.
 * Returns true only when Rollbar is properly configured and initialized.
 */
export function useRollbarEnabled(): boolean {
  return useContext(RollbarEnabledContext);
}

/**
 * RollbarProvider - Wrapper for @rollbar/react Provider.
 *
 * This client component wraps the official Rollbar Provider and creates
 * a singleton Rollbar instance that is also exposed on window.Rollbar
 * for debugging purposes.
 *
 * When Rollbar is not configured (missing access token), this provider
 * gracefully degrades - the app continues to work normally, but error
 * tracking is disabled. This is useful for local development.
 *
 * @see https://docs.rollbar.com/docs/nextjs
 */
export function RollbarProvider({ children }: RollbarProviderProps) {
  const rollbarInstance = useMemo(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const config = getClientConfig();
    if (!config.accessToken) {
      console.warn("[Rollbar] No client access token found. Client-side error tracking disabled.");
      return undefined;
    }

    const instance = new Rollbar(config);

    // Expose on window (for debugging purposes)
    if (typeof window !== "undefined") {
      (window as Window & { Rollbar?: Rollbar }).Rollbar = instance;
    }

    console.info("[Rollbar] Client-side error tracking initialized");
    return instance;
  }, []);

  if (!rollbarInstance) {
    return <RollbarEnabledContext.Provider value={false}>{children}</RollbarEnabledContext.Provider>;
  }

  return (
    <RollbarEnabledContext.Provider value={true}>
      <Provider instance={rollbarInstance}>{children}</Provider>
    </RollbarEnabledContext.Provider>
  );
}
